import { criterion, fail, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { pruefeMitMakro } from "@/office/makro/pruefung";
import { readDocument } from "@/office/word/document";
import { MAKRO_TABSTOPPS } from "../makros";

export const lektionTabstopps: Lesson = {
  id: "tabstopps",
  title: "Tabstopps, Füllzeichen und Sortieren",
  summary: "Preislisten und Inhaltszeilen, die bündig stehen – ohne fünfmal Tab zu drücken.",
  durationMinutes: 10,
  learningGoals: [
    "Tabstopps über das Lineal und den Tabstopp-Dialog setzen",
    "Rechtsbündige Tabstopps mit Füllzeichen einsetzen",
    "Eine Liste mit „Sortieren“ ordnen",
  ],
  steps: [
    {
      id: "tabstopp-setzen",
      title: "Ein Tabstopp statt vieler Tabs",
      explanation: [
        "Die Tabulatortaste springt ohne weitere Einstellung zur nächsten Standardposition, " +
          "alle 1,25 cm. Wer damit eine Preisliste baut, drückt je nach Länge des Artikels " +
          "einmal, zweimal oder dreimal Tab – und sobald sich ein Wort ändert, verrutscht die " +
          "ganze Spalte.",
        "Ein eigener Tabstopp löst das: Er legt genau eine Position fest, und ein einziger " +
          "Tab springt dorthin, egal wie lang der Text davor ist. Gesetzt wird er mit einem " +
          "Klick ins Lineal (Ansicht → Lineal) oder genau über „Start“ → Absatz-Dialog → " +
          "„Tabstopps …“.",
        "Es gibt vier Ausrichtungen. Links ist der Standard. Rechtsbündig passt für " +
          "Preise und Seitenzahlen, weil die Zahlen am rechten Rand enden. Zentriert ist " +
          "selten nützlich. Dezimal richtet Zahlen am Komma aus – ideal für Beträge mit " +
          "unterschiedlich vielen Stellen.",
      ],
      keyPoints: [
        "Ein Tab pro Spalte – die Position legt der Tabstopp fest.",
        "Rechtsbündig oder dezimal für Zahlen, links für Text.",
        "Start → Absatz → Tabstopps … setzt die Position auf den Millimeter genau.",
      ],
      task: {
        instruction:
          "Lege eine Preisliste mit mindestens drei Zeilen an: Artikel, ein einziger Tab, " +
          "Preis. Setze für diese Absätze einen rechtsbündigen (oder dezimalen) Tabstopp.",
        hint: "Alle Zeilen markieren, Start → Absatz → Tabstopps …, Position eintragen, „Rechts“ wählen, „Festlegen“.",
        macro: MAKRO_TABSTOPPS,
        check: () =>
          pruefeMitMakro(MAKRO_TABSTOPPS, (bericht) => {
            const mitTabstopp = bericht.zahl("absaetzeMitTabstopp") ?? 0;
            const rechts = (bericht.zahl("absaetzeRechtsbuendig") ?? 0) + (bericht.zahl("absaetzeDezimal") ?? 0);
            const mehrfach = bericht.zahl("absaetzeMitMehrfachTab") ?? 0;
            return fromCriteria(
              [
                criterion(`Mindestens drei Absätze mit eigenem Tabstopp (gefunden: ${mitTabstopp})`, mitTabstopp >= 3),
                criterion(`Mindestens drei davon rechtsbündig oder dezimal (gefunden: ${rechts})`, rechts >= 3),
                criterion(`Kein Absatz mit mehreren Tabs hintereinander (gefunden: ${mehrfach})`, mehrfach === 0),
              ],
              {
                success: "Die Preise stehen bündig – und bleiben es, auch wenn sich ein Artikel ändert.",
                failure: "Die Liste ist noch nicht über Tabstopps ausgerichtet.",
                hint: "Doppelte Tabs entfernen: Einer pro Zeile reicht, wenn der Tabstopp sitzt.",
              },
            );
          }),
      },
    },
    {
      id: "fuellzeichen",
      title: "Füllzeichen",
      explanation: [
        "Bei langen Abständen verliert das Auge die Zeile. Füllzeichen helfen: Punkte, " +
          "Striche oder eine Linie zwischen Text und Tabstopp. Genau so ist jedes " +
          "Inhaltsverzeichnis aufgebaut, das Word erzeugt.",
        "Füllzeichen gehören zum Tabstopp, nicht zum Text. Du stellst sie im Dialog " +
          "„Tabstopps“ unter „Füllzeichen“ ein – einmal für die markierten Absätze, oder " +
          "besser für eine Formatvorlage wie „Preisliste“.",
        "Getippte Punktreihen sind die Behelfslösung dazu. Sie treffen den rechten Rand nie " +
          "genau und verrutschen mit jeder Änderung.",
      ],
      keyPoints: [
        "Füllzeichen im Tabstopp-Dialog: Punkte, Striche oder Linie.",
        "Nie Punktreihen tippen.",
      ],
      task: {
        instruction: "Gib dem Tabstopp deiner Preisliste Punkte als Füllzeichen.",
        hint: "Zeilen markieren, Tabstopp-Dialog öffnen, den vorhandenen Tabstopp wählen, Füllzeichen „2 …..“, „Festlegen“.",
        macro: MAKRO_TABSTOPPS,
        check: async () => {
          const snapshot = await readDocument();
          const punktreihen = snapshot.filledParagraphs.filter((absatz) => /\.{6,}/.test(absatz.text)).length;
          return pruefeMitMakro(MAKRO_TABSTOPPS, (bericht) => {
            const fuell = bericht.zahl("absaetzeMitFuellzeichen") ?? 0;
            return fromCriteria(
              [
                criterion(`Mindestens drei Absätze mit Füllzeichen (gefunden: ${fuell})`, fuell >= 3),
                criterion(`Keine getippten Punktreihen (gefunden: ${punktreihen})`, punktreihen === 0),
              ],
              {
                success: "Wie im Inhaltsverzeichnis – die Punkte führen das Auge zum Preis.",
                failure: "Die Füllzeichen fehlen noch.",
                hint: "Nach Auswahl des Füllzeichens auf „Festlegen“ klicken, erst dann „OK“.",
              },
            );
          });
        },
      },
    },
    {
      id: "sortieren",
      title: "Eine Liste sortieren",
      explanation: [
        "Word sortiert nicht nur Tabellen, sondern jede Folge von Absätzen: Liste " +
          "markieren, „Start“ → „Sortieren“ (das Symbol A→Z). Im Dialog wählst du, wonach " +
          "sortiert wird – Absätze, Wörter oder bei Tabstopp-Listen auch die Felder zwischen " +
          "den Tabs – und ob als Text, Zahl oder Datum.",
        "Das ist mehr als eine Bequemlichkeit: Von Hand verschobene Zeilen gehen gern " +
          "verloren oder landen doppelt. Der Sortierdialog ändert nur die Reihenfolge.",
      ],
      keyPoints: [
        "Start → Sortieren ordnet markierte Absätze.",
        "Text, Zahl oder Datum passend wählen – sonst kommt 10 vor 9.",
      ],
      task: {
        instruction: "Sortiere deine Preisliste alphabetisch nach dem Artikelnamen.",
        hint: "Alle Zeilen der Liste markieren, Start → Sortieren, „Absätze“, „Text“, „Aufsteigend“.",
        check: async () => {
          const snapshot = await readDocument();
          const zeilen = snapshot.filledParagraphs
            .filter((absatz) => absatz.text.includes("\t"))
            .map((absatz) => absatz.text.split("\t")[0].trim());
          if (zeilen.length < 3) {
            return fail(
              "Es gibt noch keine Liste mit mindestens drei Tab-Zeilen.",
              "Leg zuerst die Preisliste aus dem ersten Schritt an.",
            );
          }
          const sortiert = zeilen.every(
            (zeile, index) => index === 0 || zeilen[index - 1].localeCompare(zeile, "de", { sensitivity: "base" }) <= 0,
          );
          return fromCriteria(
            [criterion(`Die ${zeilen.length} Zeilen stehen alphabetisch`, sortiert)],
            {
              success: "Sortiert – ohne eine einzige Zeile von Hand zu verschieben.",
              failure: `Die Reihenfolge ist noch nicht alphabetisch: ${zeilen.join(", ")}.`,
              hint: "Nur die Zeilen der Liste markieren, nicht die Überschrift darüber.",
            },
          );
        },
      },
    },
  ],
};
